import { onboardingCallbacks } from "./onboarding-callbacks";

let latestHighlightEvent = null;
window.onboardingActive = false;
const overlay = document.querySelector("#onboarding-overlay");

// Overlay helpers
const showOverlay = () => overlay?.classList.remove("hidden");
const hideOverlay = () => overlay?.classList.add("hidden");

// Remove onboarding highlights
function removeActiveElements() {
    document
        .querySelectorAll("[data-onboarding-active]")
        .forEach((el) => el.removeAttribute("data-onboarding-active"));
    resetClones();
}

// Tooltip
let currentScrollableParent = null;
let currentScrollListener = null;

function getScrollableParent(el) {
    while (el && el !== document.body) {
        const overflowY = window.getComputedStyle(el).overflowY;
        if (overflowY === "auto" || overflowY === "scroll") {
            return el;
        }
        el = el.parentElement;
    }
    return window;
}

function setTooltip(el) {
    const tooltip = document.querySelector("#onboarding-tooltip");
    if (!el || !tooltip) return;

    // Remove previous scroll listener if any
    if (currentScrollableParent && currentScrollListener) {
        currentScrollableParent.removeEventListener(
            "scroll",
            currentScrollListener
        );
        currentScrollableParent = null;
        currentScrollListener = null;
    }

    // Position tooltip
    const positionTooltip = () => {
        const rect = el.getBoundingClientRect();
        const tooltipHeight = tooltip.offsetHeight;
        const tooltipWidth = tooltip.offsetWidth;
        const tooltipOffset = 20;
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;

        tooltip.classList.remove(
            "chevron-up",
            "chevron-down",
            "chevron-left",
            "chevron-right"
        );

        let top, left, chevron;

        // Dynamically check overlap with .onboarding-controls
        const onboardingControls = document.querySelector(
            ".onboarding-controls"
        );
        let controlsRect = null;
        if (onboardingControls) {
            controlsRect = onboardingControls.getBoundingClientRect();
        }

        let wouldOverlapControls = false;
        if (controlsRect) {
            const tooltipBottom = rect.bottom + tooltipOffset + tooltipHeight;
            const tooltipRight = rect.left + tooltipWidth;
            wouldOverlapControls =
                tooltipBottom > controlsRect.top &&
                rect.left < controlsRect.right &&
                tooltipRight > controlsRect.left;
        }

        // Prefer below if there's space vertically AND horizontally
        if (
            rect.bottom + tooltipOffset + tooltipHeight <= windowHeight &&
            rect.left + tooltipWidth <= windowWidth &&
            rect.left >= 0 &&
            !wouldOverlapControls
        ) {
            left = rect.left + window.scrollX;
            top = rect.bottom + window.scrollY + tooltipOffset;
            tooltip.classList.add("chevron-up");
            chevron = "up";
        }
        // Else prefer above if there's space vertically AND horizontally
        else if (
            rect.top - tooltipOffset - tooltipHeight >= 0 &&
            rect.left + tooltipWidth <= windowWidth &&
            rect.left >= 0
        ) {
            left = rect.left + window.scrollX;
            top = rect.top + window.scrollY - tooltipHeight - tooltipOffset;
            tooltip.classList.add("chevron-down");
            chevron = "down";
        }
        // Else try right if there's space
        else if (rect.right + tooltipOffset + tooltipWidth <= windowWidth) {
            left = rect.right + window.scrollX + tooltipOffset;
            top =
                rect.top + window.scrollY + rect.height / 2 - tooltipHeight / 2;
            tooltip.classList.add("chevron-left");
            chevron = "left";
        }
        // Else fallback to left
        else {
            left = rect.left + window.scrollX - tooltipWidth - tooltipOffset;
            top =
                rect.top + window.scrollY + rect.height / 2 - tooltipHeight / 2;
            tooltip.classList.add("chevron-right");
            chevron = "right";
        }

        tooltip.style.top = `${top}px`;
        tooltip.style.left = `${left}px`;

        window.Livewire.dispatch("set-onboarding-tooltip-position", [
            { top, left, chevron },
        ]);
    };

    positionTooltip();

    // Attach scroll listener to scrollable parent
    const scrollableParent = getScrollableParent(el);
    if (scrollableParent && scrollableParent !== window) {
        currentScrollableParent = scrollableParent;
        currentScrollListener = positionTooltip;
        scrollableParent.addEventListener("scroll", positionTooltip);
    }
}

// Wait for element to appear in DOM
function waitForElement(selector, maxRetries = 10, interval = 10) {
    return new Promise((resolve, reject) => {
        let retries = 0;
        const check = () => {
            const el = document.querySelector(selector);
            if (el) {
                const rect = el.getBoundingClientRect();
                if (
                    rect.width > 0 &&
                    rect.height > 0 &&
                    (rect.top !== 0 || rect.left !== 0)
                ) {
                    return resolve(el);
                }
            }
            retries++;
            if (retries > maxRetries)
                return reject("Element not ready in time");
            setTimeout(check, interval);
        };
        check();
    });
}

function resetClones() {
    const clones = document.querySelectorAll(".onboarding-clone");
    clones.forEach((clone) => {
        clone.remove();
    });
    const originalElements = document.querySelectorAll(
        ".onboarding-clone-original"
    );
    originalElements.forEach((el) => {
        el.style.visibility = "visible";
        el.classList.remove("onboarding-clone-original");
    });
}

function applyCloneStyles(original, clone) {
    const originalChildren = original.children;
    const cloneChildren = clone.children;

    // Apply styles to the current element
    const computedStyles = window.getComputedStyle(original);
    for (const style of computedStyles) {
        clone.style[style] = computedStyles.getPropertyValue(style);
    }

    // Recursively apply styles to child elements
    for (let i = 0; i < originalChildren.length; i++) {
        applyCloneStyles(originalChildren[i], cloneChildren[i]);
    }
}

function cloneElement(el) {
    // Reset any previous clones
    resetClones();

    // TODO: make this global instead of only for summary cards
    // To make this global:
    // 1. Disable scrolling when onboarding active (due to fixed position) (keep in mind - el still needs to be scrolled into view)
    // 2. Properly apply styles, even ones that are applied later (e.g. active tab link styling after click)
    if (
        el.classList.contains("summary_card_container") &&
        window.innerWidth > 1024
    ) {
        // Clone the element
        const clone = el.cloneNode(true);

        // Get the original element's styles and position
        const rect = el.getBoundingClientRect();

        // Apply styles to the cloned element and its children
        applyCloneStyles(el, clone);

        // Position cloned element
        clone.style.position = "fixed";
        clone.style.top = `${rect.top}px`;
        clone.style.left = `${rect.left}px`;
        clone.style.width = `${rect.width}px`;
        clone.style.height = `${rect.height}px`;
        clone.classList.add("onboarding-clone");

        // Hide original element
        el.style.visibility = "hidden";
        el.classList.add("onboarding-clone-original");

        // Append cloned element to DOM root
        document.body.appendChild(clone);

        return clone;
    }
}

// Set active element (highlight)
function setActiveElement(el) {
    showOverlay();
    removeActiveElements();
    el.setAttribute("data-onboarding-active", true);

    const computedPosition = window.getComputedStyle(el).position;
    if (computedPosition === "static" || !computedPosition) {
        el.style.position = "relative";
    }

    // Clone the element to make sure it is always above the overlay
    cloneElement(el);

    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    if (document.body.scrollHeight > window.innerHeight) {
        if (rect.height > viewportHeight / 2) {
            const tooltip = document.querySelector("#onboarding-tooltip");
            const tooltipHeight = tooltip ? tooltip.offsetHeight : 0;
            const scrollY = window.scrollY + rect.top - tooltipHeight - 40; // 40px gap
            window.scrollTo({
                top: scrollY,
                behavior: "smooth",
            });
        } else {
            el.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }

    setTooltip(el);
}

// Main highlight logic
async function highlightElement(event) {
    if (!window.onboardingActive) return;

    // Get data from livewire
    const step = event.detail[0]?.current_step.highlighted_element;
    const currentStepOrder = event.detail[0]?.current_step.order;
    const eventType = event.detail[0]?.event_type;
    const callbackKey = event.detail[0]?.current_step?.callback_key;

    // Run the callback if it exists
    if (callbackKey && typeof onboardingCallbacks[callbackKey] === "function") {
        onboardingCallbacks[callbackKey]();
    }

    // Store active onboarding ID globally if provided
    const activeOnboardingId = event.detail[0]?.onboarding_id;
    if (typeof activeOnboardingId !== "undefined") {
        window.activeOnboardingId = activeOnboardingId;
    }

    // Store total steps globally if provided
    const totalSteps = event.detail[0]?.total_steps;
    if (typeof totalSteps !== "undefined") {
        window.onboardingTotalSteps = totalSteps;
    }

    // Disable previous button on step 2 if step 1 is null
    if (currentStepOrder === 2 && window.onboardingInitialStepIsNull) {
        const previousButton = document.querySelector(
            "#onboarding-previous-button"
        );
        if (previousButton) previousButton.setAttribute("disabled", true);
        // Don't sync with Livewire for now, doesn't work as smooth as JS
        // window.Livewire.dispatch("disable-onboarding-previous-button");
    }

    // Wait for the element to appear
    const selector = `[data-onboarding-step="${step}"]`;
    try {
        const el = await waitForElement(selector);
        if (el) {
            // const elIsHidden =
            //     window.getComputedStyle(el).display === "none" ||
            //     window.getComputedStyle(el).visibility === "hidden";
            // if (elIsHidden) throw new Error("Element is hidden");
            setActiveElement(el);
            addOnboardingListeners();
        } else {
            throw new Error("Element not found");
        }
    } catch (err) {
        console.warn(err);

        // Handle element not found or hidden
        if (eventType === "initial") {
            window.onboardingInitialStepIsNull = true;
            window.Livewire.dispatch("onboarding-next-step-from-js", [
                window.onboardingTotalSteps,
            ]);
        } else if (eventType === "previous" && currentStepOrder > 1) {
            window.Livewire.dispatch("onboarding-previous-step-from-js");
        } else if (eventType === "next") {
            if (window.onboardingTotalSteps === currentStepOrder) {
                window.Livewire.dispatch("onboarding-finished-from-js", [
                    window.activeOnboardingId,
                ]);
            } else {
                window.Livewire.dispatch("onboarding-next-step-from-js", [
                    window.onboardingTotalSteps,
                ]);
            }
        }
    }
}

// Check if element for final step is an element with [data-onboarding-finish] and dispatch finished on submit
const addOnboardingFinishListeners = () => {
    const onboardingFinishElements = document.querySelectorAll(
        "[data-onboarding-finish]"
    );
    onboardingFinishElements.forEach((el) => {
        el.removeEventListener("submit", onboardingFinishHandler);
        el.addEventListener("submit", onboardingFinishHandler);
    });
};

const onboardingFinishHandler = (event) => {
    if (!event.isTrusted) return; // Ignore programmatic submits
    if (
        window.activeOnboardingId &&
        window.onboardingActive &&
        event.currentTarget.getAttribute("data-onboarding-active") === "true"
    ) {
        window.Livewire.dispatch("onboarding-finished-from-js", [
            window.activeOnboardingId,
        ]);
    }
};

// Check if element for step is an element with [data-onboarding-next-step] and dispatch next step on click
const addOnboardingNextStepListeners = () => {
    const onboardingNextStepElements = document.querySelectorAll(
        "[data-onboarding-next-step]"
    );
    onboardingNextStepElements.forEach((el) => {
        el.removeEventListener("click", onboardingNextStepHandler);
        el.addEventListener("click", onboardingNextStepHandler);
    });
};

const onboardingNextStepHandler = (event) => {
    if (!event.isTrusted) return; // Ignore programmatic clicks
    if (
        window.onboardingTotalSteps &&
        window.onboardingActive &&
        event.currentTarget.getAttribute("data-onboarding-active") === "true"
    ) {
        event.preventDefault(); // Prevent default action to make sure we handle it with Livewire
        window.Livewire.dispatch("onboarding-next-step-from-js", [
            window.onboardingTotalSteps,
        ]);
    }
};

// Group onboarding listeners
const addOnboardingListeners = () => {
    addOnboardingFinishListeners();
    addOnboardingNextStepListeners();
};

// Cleanup
function hideOnboarding() {
    hideOverlay();
    removeActiveElements();
    window.onboardingActive = false;
}

// Event listeners
document.addEventListener("livewire:navigated", () => {
    if (latestHighlightEvent)
        setTimeout(() => highlightElement(latestHighlightEvent), 0);
});

window.addEventListener("onboarding-step-changed", (event) => {
    showOverlay();
    latestHighlightEvent = event;
    window.onboardingActive = true;
    setTimeout(() => highlightElement(event), 0);
});

window.addEventListener("resize", () => {
    if (latestHighlightEvent) highlightElement(latestHighlightEvent);
});

window.addEventListener("onboarding-finished", hideOnboarding);
window.addEventListener("onboarding-skipped", hideOnboarding);
